import {Fragment, type ReactNode} from 'react'
import {useTranslation} from 'react-i18next'
import {cn} from '@/lib/utils'
import {Checkbox} from '@/components/ui/checkbox'
import type {SortDir} from '@/hooks/common/use-table-state'

type RowSelection<T> = {
  selected: Set<string>
  onChange: (next: Set<string>) => void
  isDisabled?: (row: T) => boolean
}

export type Column<T> = {
  key: string
  /** El tipo del dato en una o dos palabras (U20); una frase que describe varias columnas va en `columnGroups`. */
  header: ReactNode
  /** Fuera del botón de ordenar: un `HelpIcon` dentro sería un botón dentro de otro (U4). */
  headerAside?: ReactNode
  sortable?: boolean
  /** Applied to both `<th>` and `<td>` — alignment/color, never width (use `width`). */
  className?: string
  /** Share of the table under `table-fixed`; give the free-text column the largest one. */
  width?: string
  /** Clase `left-*` donde queda pegada (`w-56` → `left-56` para la siguiente). La superficie opaca
   * la pone el componente. */
  sticky?: string
  /** Para la columna con botón propio: sin esto el mismo clic también dispara `onRowClick`. */
  stopClick?: boolean
  cell: (row: T) => ReactNode
}

function Wrap({con, children}: {con: ReactNode; children: ReactNode}) {
  if (!con) return <>{children}</>
  return (
    <span className="inline-flex items-center gap-1">
      {children}
      {con}
    </span>
  )
}

type DataTableProps<T> = {
  columns: Column<T>[]
  rows: T[]
  rowKey: (row: T) => string
  rowTestId?: (row: T) => string
  /** `data-highlight-id` que busca `hooks/use-highlight-param.ts`. */
  rowHighlightId?: (row: T) => string
  sortBy?: string
  sortDir?: SortDir
  onSort?: (key: string) => void
  onRowClick?: (row: T) => void
  emptyState?: ReactNode
  selection?: RowSelection<T>
  /** Debajo de las columnas, a todo el ancho; nunca una columna más (R2). */
  renderRowDetail?: (row: T) => ReactNode
  columnGroups?: ColumnGroup[]
  /** Longitud CSS (`'72rem'`), no clase: sale de las columnas del servidor y Tailwind no compila
   * clases armadas en tiempo de ejecución. */
  minWidth?: string
}

/** Para dos o más columnas seguidas con un prefijo común (U20); el criterio de una sola columna
 * va en su `headerAside`. */
export type ColumnGroup = {
  /** Una `Column.key` real; los grupos van en el mismo orden que las columnas. */
  startKey: string
  /** Nunca 1: una columna sola lleva su criterio en `headerAside`. */
  span: number
  content: ReactNode
}

/** `table-fixed` keeps one long cell from widening its column over the others; `break-words` on
 * cells and headers is the safety net. */
export function DataTable<T>({
  columns,
  rows,
  rowKey,
  rowTestId,
  rowHighlightId,
  sortBy,
  sortDir,
  onSort,
  onRowClick,
  emptyState,
  selection,
  renderRowDetail,
  columnGroups,
  minWidth,
}: DataTableProps<T>) {
  const {t} = useTranslation()
  if (rows.length === 0 && emptyState) return <>{emptyState}</>

  // Lo que ningún grupo cubre es un `<th>` en blanco: la fila de grupos siempre tiene tantas
  // celdas como columnas.
  const groupCells: {key: string; group: ColumnGroup | null; sticky?: string}[] = []
  if (columnGroups) {
    let idx = 0
    while (idx < columns.length) {
      const col = columns[idx]
      const group = columnGroups.find((g) => g.startKey === col.key)
      // Hereda el congelado de su columna o el rótulo se desliza sobre la columna quieta.
      groupCells.push({key: col.key, group: group ?? null, sticky: col.sticky})
      idx += group?.span ?? 1
    }
  }

  const selectableRows = selection ? rows.filter((row) => !selection.isDisabled?.(row)) : []
  const selectedCount = selection ? selectableRows.filter((row) => selection.selected.has(rowKey(row))).length : 0
  const allSelected = selectableRows.length > 0 && selectedCount === selectableRows.length

  function toggleAll() {
    if (!selection) return
    const next = new Set(selection.selected)
    if (allSelected) for (const row of selectableRows) next.delete(rowKey(row))
    else for (const row of selectableRows) next.add(rowKey(row))
    selection.onChange(next)
  }

  function toggleRow(row: T) {
    if (!selection) return
    const key = rowKey(row)
    const next = new Set(selection.selected)
    if (next.has(key)) next.delete(key)
    else next.add(key)
    selection.onChange(next)
  }

  return (
    <div className="w-full overflow-x-auto">
      <table className="w-full table-fixed text-sm" style={minWidth ? {minWidth} : undefined}>
        <colgroup>
          {selection && <col className="w-10" />}
          {columns.map((col) => (
            <col key={col.key} className={col.width} />
          ))}
        </colgroup>
        <thead>
          {columnGroups && (
            <tr className="bg-muted/55 text-left text-muted-foreground">
              {selection && <th className="rounded-l-md py-1.5 pl-3" />}
              {groupCells.map(({key, group, sticky}) => (
                <th
                  key={key}
                  colSpan={group?.span ?? 1}
                  className={cn(
                    'py-1.5 text-left align-bottom first:rounded-l-md first:pl-3 last:rounded-r-md last:pr-3',
                    group && 'surface-divider border-b',
                    sticky && ['sticky z-20 bg-muted', sticky]
                  )}>
                  {group?.content}
                </th>
              ))}
            </tr>
          )}
          <tr className="surface-divider border-b bg-muted/55 text-left text-muted-foreground">
            {selection && (
              <th className={cn('py-2.5 pl-3', !columnGroups && 'rounded-l-md')}>
                <Checkbox
                  checked={allSelected}
                  indeterminate={selectedCount > 0 && !allSelected}
                  onCheckedChange={toggleAll}
                  aria-label={t('common.seleccionarTodo')}
                  data-testid="select-all"
                />
              </th>
            )}
            {columns.map((col) => (
              <th
                key={col.key}
                className={cn(
                  'py-2.5 font-mono text-[11px] font-semibold tracking-[0.12em] uppercase break-words first:pl-3 last:pr-3',
                  !columnGroups && 'first:rounded-l-md last:rounded-r-md',
                  // Opaco, no `bg-muted/55`: un encabezado congelado traslúcido deja ver pasar los de al lado.
                  col.sticky && ['sticky z-20 bg-muted', col.sticky],
                  col.className
                )}>
                {/* Sin `headerAside` el `th` conserva el markup de antes y no mueve líneas base visuales. */}
                <Wrap con={col.headerAside}>
                  {col.sortable ? (
                    <button
                      type="button"
                      onClick={() => onSort?.(col.key)}
                      className="inline-flex items-center gap-1 tracking-wider uppercase break-words hover:text-foreground">
                      {col.header}
                      {sortBy === col.key && <span aria-hidden="true">{sortDir === 'desc' ? '↓' : '↑'}</span>}
                    </button>
                  ) : (
                    col.header
                  )}
                </Wrap>
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {rows.map((row) => {
            const detalle = renderRowDetail?.(row)
            return (
              <Fragment key={rowKey(row)}>
                <tr
                  data-testid={rowTestId?.(row)}
                  data-highlight-id={rowHighlightId?.(row)}
                  onClick={() => onRowClick?.(row)}
                  tabIndex={onRowClick ? 0 : undefined}
                  role={onRowClick ? 'link' : undefined}
                  onKeyDown={
                    onRowClick
                      ? (e) => {
                          // Solo la tecla que nace en la fila: Enter en un botón de celda no debe abrirla (KAI-334).
                          if (e.target !== e.currentTarget) return
                          if (e.key === 'Enter' || e.key === ' ') {
                            e.preventDefault()
                            onRowClick(row)
                          }
                        }
                      : undefined
                  }
                  className={cn(
                    'surface-divider border-b transition-colors',
                    !detalle && 'last:border-0',
                    detalle && 'border-b-0',
                    onRowClick &&
                      'cursor-pointer outline-none hover:bg-muted/55 focus-visible:bg-muted/55 focus-visible:ring-2 focus-visible:ring-ring/50 focus-visible:ring-inset'
                  )}>
                  {selection && (
                    <td className="py-3 pl-3 align-top" onClick={(e) => e.stopPropagation()}>
                      <Checkbox
                        checked={selection.selected.has(rowKey(row))}
                        disabled={selection.isDisabled?.(row)}
                        onCheckedChange={() => toggleRow(row)}
                        aria-label={t('common.seleccionarFila')}
                      />
                    </td>
                  )}
                  {columns.map((col) => (
                    <td
                      key={col.key}
                      className={cn(
                        'py-3 align-top break-words first:pl-3 last:pr-3',
                        col.sticky && ['sticky z-10 bg-[var(--background)]', col.sticky],
                        col.className
                      )}
                      onClick={col.stopClick ? (e) => e.stopPropagation() : undefined}>
                      {col.cell(row)}
                    </td>
                  ))}
                </tr>
                {detalle && (
                  <tr className="surface-divider border-b transition-colors last:border-0">
                    <td colSpan={columns.length + (selection ? 1 : 0)} className="px-3 pb-3">
                      {detalle}
                    </td>
                  </tr>
                )}
              </Fragment>
            )
          })}
        </tbody>
      </table>
    </div>
  )
}
